import React, { useState, ChangeEvent, FormEvent } from 'react';
import { motion } from 'framer-motion';
import PageLayout from '../components/PageLayout';
import { FileText, X, Loader2, CheckCircle, AlertTriangle, User, Mail, Phone, Briefcase, FileUp } from 'lucide-react';
import CollapsibleSection from '../components/CollapsibleSection';
import { supabase } from '../lib/supabase';

const softwareOptions = ["AutoCAD", "3ds Max", "SketchUp", "Lumion", "Blender", "V-Ray", "Revit"];

interface FilePreview {
  name: string;
  size: number;
  type: string;
  source: string;
}

const Careers = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
  });
  const [software, setSoftware] = useState<string[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<FilePreview[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState<string | null>(null);

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = e.target;
    setSoftware(prev =>
      checked ? [...prev, value] : prev.filter(s => s !== value)
    );
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...newFiles]);

      const newPreviews = newFiles.map(file => {
        const reader = new FileReader();
        return new Promise<FilePreview>(resolve => {
          reader.onload = () => {
            resolve({
              name: file.name,
              size: file.size,
              type: file.type,
              source: reader.result as string,
            });
          };
          reader.readAsDataURL(file);
        });
      });

      Promise.all(newPreviews).then(previews => {
        setFilePreviews(prev => [...prev, ...previews]);
      });
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
    setFilePreviews(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setSubmissionStatus('idle');

    if (!formData.name || !formData.email || !formData.experience || files.length === 0) {
      setFormError('Please fill in all required fields and upload your CV/Portfolio.');
      return;
    }

    setIsSubmitting(true);

    try {
      const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
      const uploadPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;

      if (!cloudName || !uploadPreset || cloudName === "YOUR_CLOUDINARY_CLOUD_NAME") {
        throw new Error("Cloudinary environment variables are not configured. Please contact the site administrator.");
      }

      const uploadedFiles = await Promise.all(
        files.map(async (file) => {
          const uploadFormData = new FormData();
          uploadFormData.append('file', file);
          uploadFormData.append('upload_preset', uploadPreset);

          const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/upload`, {
            method: 'POST',
            body: uploadFormData,
          });

          if (!response.ok) {
            const errorData = await response.json();
            throw new Error(`File upload failed: ${errorData.error.message}`);
          }

          const data = await response.json();
          return {
            name: file.name,
            url: data.secure_url,
            type: file.type,
            size: file.size,
          };
        })
      );

      const { error: insertError } = await supabase.from('applications').insert([
        {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          experience: parseInt(formData.experience, 10),
          software_expertise: software,
          files: uploadedFiles,
        },
      ]);

      if (insertError) {
        throw insertError;
      }

      setSubmissionStatus('success');
      // Reset form
      setFormData({ name: '', email: '', phone: '', experience: '' });
      setSoftware([]);
      setFiles([]);
      setFilePreviews([]);

    } catch (error: unknown) {
      console.error("Submission error:", error);
      let errorMessage = 'An unexpected error occurred during submission.';
      if (error instanceof Error) {
        if (error.message.includes('unsigned uploads')) {
          errorMessage = "Configuration Error: The Cloudinary upload preset is not configured for unsigned uploads. Please check your Cloudinary settings.";
        } else {
          errorMessage = error.message;
        }
      }
      setFormError(errorMessage);
      setSubmissionStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const FilePreviewCard = ({ file, index, onRemove }: { file: FilePreview, index: number, onRemove: (index: number) => void }) => (
    <div className="relative group bg-gray-50 p-3 rounded-lg flex items-center space-x-3 border border-gray-200">
      <div className="flex-shrink-0">
        {file.type.startsWith('image/') ? (
          <img src={file.source} alt={file.name} className="w-12 h-12 rounded-md object-cover" />
        ) : (
          <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center">
            <FileText className="w-6 h-6 text-gray-500" />
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
        <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
      </div>
      <button
        type="button"
        onClick={() => onRemove(index)}
        className="absolute top-1 right-1 p-1 rounded-full bg-gray-300/50 text-gray-600 opacity-0 group-hover:opacity-100 hover:bg-red-100 hover:text-red-600 transition-all"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );

  if (submissionStatus === 'success') {
    return (
      <PageLayout title="Thank You!" subtitle="Your application has been received.">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center bg-white p-12 rounded-2xl shadow-lg max-w-2xl mx-auto"
        >
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
          <h2 className="text-2xl font-bold mb-4">Application Submitted Successfully!</h2>
          <p className="text-gray-600">
            We've received your application for the 3D Visualizer position. Our team will review your profile and get in touch if you're a good fit. We appreciate your interest in joining Master Home!
          </p>
        </motion.div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title="Bring Our Homes to Life"
      subtitle="We’re Hiring a 3D Visualizer!"
    >
      <div className="flex flex-col lg:grid lg:grid-cols-5 gap-12 lg:gap-16 items-start">
        
        <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="w-full lg:col-span-3 order-2 lg:order-1 bg-white p-8 rounded-2xl shadow-lg border border-gray-100"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            <h2 className="text-2xl font-bold mb-4">Apply Now</h2>
            
            <div className="space-y-4">
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="text" name="name" placeholder="Full Name" required value={formData.name} onChange={handleInputChange} className="input-field pl-10" />
              </div>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="email" name="email" placeholder="Email Address" required value={formData.email} onChange={handleInputChange} className="input-field pl-10" />
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="tel" name="phone" placeholder="Phone Number" value={formData.phone} onChange={handleInputChange} className="input-field pl-10" />
              </div>
              <div className="relative">
                <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="number" name="experience" placeholder="Years of Experience" required value={formData.experience} onChange={handleInputChange} className="input-field pl-10" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Software Expertise</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {softwareOptions.map(s => (
                  <label key={s} className="flex items-center space-x-2 text-sm text-gray-600 cursor-pointer p-2 rounded-md hover:bg-gray-50">
                    <input type="checkbox" value={s} checked={software.includes(s)} onChange={handleCheckboxChange} className="h-4 w-4 rounded text-primary-600 focus:ring-primary-500 border-gray-300" />
                    <span>{s}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">CV & Portfolio</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <FileUp className="mx-auto h-12 w-12 text-gray-400" />
                  <div className="flex text-sm text-gray-600">
                    <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-primary-600 hover:text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary-500">
                      <span>Upload files</span>
                      <input id="file-upload" name="files" type="file" className="sr-only" multiple onChange={handleFileChange} accept=".pdf,.doc,.docx,image/*,video/*" />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">PDF, DOC, Images, Video up to 25MB each</p>
                </div>
              </div>
              {filePreviews.length > 0 && (
                <div className="mt-4 space-y-2">
                  {filePreviews.map((file, index) => (
                    <FilePreviewCard key={index} file={file} index={index} onRemove={removeFile} />
                  ))}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Role</label>
              <input type="text" value="3D Visualizer" disabled className="input-field bg-gray-100 text-gray-500 cursor-not-allowed" />
            </div>
            
            {(formError || submissionStatus === 'error') && (
              <div className="flex items-center text-sm text-red-600 bg-red-50 p-3 rounded-lg">
                <AlertTriangle className="w-4 h-4 mr-2" />
                {formError || 'An unexpected error occurred.'}
              </div>
            )}

            <button type="submit" disabled={isSubmitting} className="btn-primary w-full flex items-center justify-center gap-2">
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Application'
              )}
            </button>
          </form>
        </motion.div>

        <div className="w-full lg:col-span-2 order-1 lg:order-2 space-y-6">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <h2 className="text-3xl font-bold text-gradient mb-4">Job Snapshot</h2>
              <p className="text-gray-600 leading-relaxed">
                As a 3D Visualizer at Master Home, you will be at the forefront of bringing our architectural designs to life. You'll create stunning, photorealistic renderings for our custom residential and renovation projects, helping clients visualize their dream homes before construction even begins.
              </p>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
                <CollapsibleSection title="Key Responsibilities">
                    <ul className="space-y-2 text-gray-600 list-disc list-inside">
                        <li>Develop high-quality 3D models, renderings, and animations.</li>
                        <li>Collaborate with architects and designers to accurately interpret plans.</li>
                        <li>Ensure all visualizations align with our brand's standard of excellence.</li>
                        <li>Manage multiple projects and deadlines effectively.</li>
                    </ul>
                </CollapsibleSection>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
                <CollapsibleSection title="Qualifications" defaultOpen={true}>
                    <ul className="space-y-2 text-gray-600 list-disc list-inside">
                        <li><strong className="font-medium text-gray-800">3+ years</strong> of professional experience in architectural visualization.</li>
                        <li>A strong portfolio showcasing residential or renovation projects.</li>
                        <li>Proficiency in 3D modeling, texturing, lighting, and rendering.</li>
                        <li>Expertise in software like <strong className="font-medium text-gray-800">3ds Max, SketchUp, Lumion, V-Ray, or Blender</strong>.</li>
                    </ul>
                </CollapsibleSection>
            </motion.div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Careers;
